import type { HandBreak, Seat } from "../gen/bazivan/v1/table_pb";
import { client } from "../net/client";
import { signal } from "../net/signal";
import { botBare } from "./seats";
import type { Sequencer } from "../scene/queue";

/**
 * مکثِ میانِ دو دست — «ادامه» ِ همه.
 *
 * پیش‌تر سرور بعد از هر دست هفت ثانیه صبر می‌کرد و خودش دستِ بعد را پخش می‌کرد، و دکمه‌ی
 * «دستِ بعد» ِ پنل فقط پنل را می‌بست (کاربر، ۲۳ اکتبر ۲۰۲۶: «بعد از هر بازی فرصت به بازیکن
 * نمی‌دی»). حالا پنلِ نتیجه باز می‌ماند و دکمه به سرور می‌گوید «آماده‌ام»؛ دستِ بعد وقتی پخش
 * می‌شود که همه زده باشند، یا مهلت تمام شود. Backend/internal/table/handbreak.go.
 *
 * هر کنترلرِ میزِ چنددستی یکی از این دارد و با هر وضعیت `update` را صدا می‌زند؛ پنل از
 * [[BreakNext]] می‌کشد.
 */
export interface BreakView {
  /** به ترتیبِ صندلیِ سرور: آماده است؟ */
  ready: boolean[];
  /** نامِ هر صندلی، به همان ترتیب — ربات بی واژه‌ی «ربات» ([[botBare]]). */
  names: string[];
  /** ربات است؟ — پنل جای واژه آیکون می‌کشد. */
  bots: boolean[];
  /** صندلیِ من در سرور؛ −۱ = تماشاچی. */
  me: number;
  /** performance.now() ِ پخشِ خودکارِ دستِ بعد. */
  endsAt: number;
  /** «ادامه» را زده‌ام — پیش از رسیدنِ تأییدِ سرور هم. */
  pressed: boolean;
  /** شماره‌ی همین مکث — HandBreak.no. */
  no: number;
}

/**
 * لحظه‌ی رسیدنِ هر HandBreak از سوکت (performance.now()).
 *
 * کنترلر وضعیت را بعد از انیمیشنِ پایانِ دست می‌نشاند، نه همان لحظه‌ی رسیدن؛ left_ms ولی از
 * لحظه‌ی فرستادن است. اگر ثانیه‌شمار از لحظه‌ی نشستن حساب می‌شد، به اندازه‌ی همان انیمیشن جلو
 * می‌افتاد: تماشاچیِ تولید «۷» می‌دید و پنل دو ثانیه بعد بسته شد (۲۳ اکتبر ۲۰۲۶).
 */
const arrived = new WeakMap<HandBreak, number>();
client.on((env) => {
  const v = env.msg.value as { brk?: HandBreak } | undefined;
  if (v && typeof v === "object" && v.brk) arrived.set(v.brk, performance.now());
});

/**
 * پنلِ نتیجه دستِ کم این‌قدر دیده شود، مگر خودِ بیننده «ادامه» را زده باشد — همان هفت ثانیه‌ی
 * HandPause ِ پیشین.
 *
 * سرور منتظرِ «ادامه» ِ بازیکن‌ها می‌ماند، ولی نمی‌داند صفحه‌ی هر بیننده چقدر عقب است: در
 * کندترین سرعت، تماشاچی چند ثانیه پشتِ سرور می‌ماند و وقتی پنل برایش باز می‌شد، میز دستِ بعد
 * را تقریباً پخش کرده بود — در تولید پنل ۲٫۱ ثانیه دیده شد (۲۳ اکتبر ۲۰۲۶، میزِ جمعیت). پس صفحه
 * خودش وضعیتِ دستِ تازه را تا این مدت پشتِ پنل نگه می‌دارد ([[Sequencer.linger]]).
 */
export const PANEL_MIN_MS = 7000;

/** رویدادهایی که پنلِ نتیجه را باز می‌کنند؛ صف هیچ‌وقت از رویشان نمی‌پرد ([[Sequencer.enqueue]] keep). */
export const RESULT_EVENTS: ReadonlySet<string> = new Set(["hand_over", "round_over", "match_over"]);

export class BreakWatch {
  readonly view = signal<BreakView | null>(null);
  /** performance.now() ِ بازشدنِ پنلِ همین مکث؛ ۰ = پنلی باز نیست. */
  private shownAt = 0;
  /** شماره‌ی آخرین مکثی که به صف رسیده — [[mustShow]]. */
  private queuedNo = 0;

  /**
   * این وضعیت اولینِ مکثِ تازه است؟ آن‌وقت صف نباید از رویش بپرد (keep): پنلِ هفت خبیث با همین
   * وضعیت باز می‌شود و مهلت و تیک‌های همه با آن می‌آیند. موقعِ گذاشتن در صف صدا زده می‌شود.
   */
  mustShow(brk: HandBreak | undefined): boolean {
    if (!brk || brk.no <= this.queuedNo) return false;
    this.queuedNo = brk.no;
    return true;
  }
  /** شماره‌ی مکثی که «ادامه» ِ آن را زده‌ام؛ −۱ = زدم پیش از رسیدنِ وضعیتِ مکث. */
  private pressedNo = 0;

  /** هر وضعیتِ تازه‌ی میز. brk نیست = وسطِ بازی (یا سرورِ کهنه). */
  update(brk: HandBreak | undefined, seats: Seat[], me: number, watching: boolean): void {
    if (!brk) { this.view.value = null; this.shownAt = 0; return; }
    if (!this.view.value || this.view.value.no !== brk.no) this.shownAt = performance.now();
    if (this.pressedNo === -1) this.pressedNo = brk.no;
    const seat = watching ? -1 : me;
    this.view.value = {
      ready: [...brk.ready],
      names: seats.map((s) => botBare(s.name, s.bot) || "بازیکن"),
      bots: seats.map((s) => s.bot),
      me: seat,
      endsAt: (arrived.get(brk) ?? performance.now()) + brk.leftMs,
      pressed: seat >= 0 && (this.pressedNo === brk.no || !!brk.ready[seat]),
      no: brk.no,
    };
  }

  /**
   * چند میلی‌ثانیه‌ی دیگر پنل باید بماند پیش از نشاندنِ وضعیتی با این مکث (next)؛ ۰ = همین حالا.
   *
   * فقط وقتی مکث تمام شده (next نیست)، پنل واقعاً روی صفحه بوده (panelUp) و بیننده خودش
   * «ادامه» نزده — کسی که زده منتظرِ دستِ بعد است و نباید معطل شود.
   */
  holdMs(next: HandBreak | undefined, panelUp: boolean): number {
    const v = this.view.value;
    if (next || !panelUp || !v || v.pressed || this.shownAt <= 0) return 0;
    return Math.max(0, this.shownAt + PANEL_MIN_MS - performance.now());
  }

  /**
   * پیش از اولین گامِ دستِ تازه — وضعیتِ بی‌مکث، یا رویدادِ «deal»/«round_start» که پیش از آن
   * می‌رسد: پنل را تا [[holdMs]] نگه دار، با درنگی که صف عقب‌ماندگی نمی‌شمارد ([[Sequencer.linger]]).
   */
  async keep(seq: Sequencer, next: HandBreak | undefined, panelUp: boolean): Promise<void> {
    const ms = this.holdMs(next, panelUp);
    if (ms > 0) await seq.linger(new Promise<void>((r) => setTimeout(r, ms)));
  }

  /**
   * «ادامه». false یعنی سرور مکثِ «ادامه» ندارد (پروتکلِ ۱) و پنل باید همان‌جا بسته شود —
   * رفتارِ پیشین؛ دستِ بعد را سرور با زمان‌سنجِ خودش می‌آورد.
   */
  press(): boolean {
    if (client.serverProtocol < 2) return false;
    const v = this.view.value;
    if (v?.me === -1) return true;   // تماشاچی دکمه ندارد
    if (v?.pressed) return true;
    client.handReady();
    if (!v) { this.pressedNo = -1; return true; }
    this.pressedNo = v.no;
    this.view.value = { ...v, pressed: true, ready: v.ready.map((r, i) => r || i === v.me) };
    return true;
  }
}
